/**
 * Ads Store — Google Ads dashboard data
 *
 * Fetches campaign performance, search terms, geo data, and change history.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CampaignMetrics {
  campaign_id: string;
  campaign_name: string;
  campaign_status: string;
  bidding_strategy: string;
  impressions: number;
  clicks: number;
  cost_eur: number;
  conversions: number;
  conversion_value_eur: number;
  cpa: number | null;
  avg_impression_share: number | null;
  avg_budget_lost_is: number | null;
  avg_rank_lost_is: number | null;
  cost_delta: number | null;
  conv_delta: number | null;
}

export interface PerformanceTotals {
  cost_eur: number;
  conversions: number;
  clicks: number;
  impressions: number;
  cpa: number | null;
  prev_cpa: number | null;
}

export interface SearchTermRow {
  search_term: string;
  matched_keyword: string;
  campaigns: string;
  impressions: number;
  clicks: number;
  cost_eur: number;
  conversions: number;
  conversion_value_eur: number;
  cpa: number | null;
  is_waste: boolean;
}

export interface GeoRow {
  province: string;
  impressions: number;
  clicks: number;
  cost_eur: number;
  conversions: number;
  conversion_value_eur: number;
  cpa: number | null;
  ctr: number;
}

export interface ChangeRow {
  change_datetime: string;
  user_email: string;
  client_type: string;
  resource_type: string;
  operation: string;
  campaign_name: string;
  changed_fields: string | null;
}

export interface WindowMetrics {
  days: number;
  cost_eur: number | null;
  conversions: number | null;
  cpa: number | null;
}

export interface OptimizationRow {
  id: number;
  changed_at: string;
  actor: string;
  category: string;
  campaign_name: string | null;
  change_summary: string;
  reason: string | null;
  expected_effect: string | null;
  evaluate_on: string | null;
  evaluated_at: string | null;
  verdict: string | null;
  verdict_status: 'open' | 'success' | 'neutral' | 'failed' | 'reverted' | 'closed' | 'proposed' | 'vetoed' | 'executed';
  computed_before: WindowMetrics | null;
  computed_after: WindowMetrics | null;
  // Ads-autonomie (migratie 151)
  source: string | null;
  gate: unknown | null;
  gate_hit_at: string | null;
  execute_after: string | null;
  // Gate-klok trede 2 (migratie 152)
  beoordeling: 'akkoord' | 'afgekeurd' | null;
  beoordeeld_door: string | null;
  beoordeeld_at: string | null;
  beoordeling_reden: string | null;
}

interface AdsState {
  campaigns: CampaignMetrics[];
  totals: PerformanceTotals | null;
  searchTerms: SearchTermRow[];
  searchTermSummary: { total_terms: number; waste_terms: number; waste_cost_eur: number } | null;
  geoData: GeoRow[];
  changes: ChangeRow[];
  optimizations: OptimizationRow[];
  isLoading: boolean;
  error: string | null;
  dateRange: number;
  activeTab: 'overview' | 'search-terms' | 'geo' | 'changes' | 'optimizations';
  setDateRange: (days: number) => void;
  setActiveTab: (tab: AdsState['activeTab']) => void;
  fetchAll: () => Promise<void>;
  besluitProposal: (changeId: number, besluit: 'veto' | 'akkoord', reden?: string) => Promise<string | null>;
  beoordeel: (changeId: number, beoordeling: 'akkoord' | 'afgekeurd', reden?: string) => Promise<string | null>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function apiFetch(url: string): Promise<Response> {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');
  return fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useAdsStore = create<AdsState>()((set, get) => ({
  campaigns: [],
  totals: null,
  searchTerms: [],
  searchTermSummary: null,
  geoData: [],
  changes: [],
  optimizations: [],
  isLoading: false,
  error: null,
  dateRange: 30,
  activeTab: 'overview',

  setDateRange: (days: number) => {
    set({ dateRange: days });
    get().fetchAll();
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  besluitProposal: async (changeId, besluit, reden) => {
    try {
      const token = await getToken();
      if (!token) return 'Niet ingelogd';
      const res = await fetch('/api/ads/proposal-besluit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ changeId, besluit, reden }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return body.error || `Fout (${res.status})`;
      await get().fetchAll();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : 'Netwerkfout';
    }
  },

  beoordeel: async (changeId, beoordeling, reden) => {
    try {
      const token = await getToken();
      if (!token) return 'Niet ingelogd';
      const res = await fetch('/api/ads/beoordeling', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ changeId, beoordeling, reden }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return body.error || `Fout (${res.status})`;
      await get().fetchAll();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : 'Netwerkfout';
    }
  },

  fetchAll: async () => {
    set({ isLoading: true, error: null });
    const days = get().dateRange;

    try {
      const [perfRes, termsRes, geoRes, changesRes, optsRes] = await Promise.all([
        apiFetch(`/api/ads/performance?days=${days}`),
        apiFetch(`/api/ads/search-terms?days=${Math.min(days, 14)}`),
        apiFetch(`/api/ads/geo?days=${days}`),
        apiFetch('/api/ads/changes?days=14'),
        apiFetch('/api/ads/optimizations'),
      ]);

      if (!perfRes.ok) throw new Error(`Performance API: ${perfRes.status}`);

      const perf = await perfRes.json();
      const terms = termsRes.ok ? await termsRes.json() : { terms: [], summary: null };
      const geo = geoRes.ok ? await geoRes.json() : { geo: [] };
      const ch = changesRes.ok ? await changesRes.json() : { changes: [] };
      const opts = optsRes.ok ? await optsRes.json() : { optimizations: [] };

      set({
        campaigns: perf.campaigns || [],
        totals: perf.totals || null,
        searchTerms: terms.terms || [],
        searchTermSummary: terms.summary || null,
        geoData: geo.geo || [],
        changes: ch.changes || [],
        optimizations: opts.optimizations || [],
        isLoading: false,
      });
    } catch (error) {
      console.error('[Ads] fetchAll error:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Fout bij laden',
      });
    }
  },
}));
